import { useCallback, useEffect, useMemo, useState } from "react";
import { DEFAULT_AUDIO_TONE_BY_CHANNEL } from "../constants/audioCatalog";
import { getAudioPreferenceKey } from "../helpers/getAudioPreferenceKey";
import { AudioChannelPreference, NormalizedAudioSettingsItem, AudioToneId } from "../types";

type UseAudioPreferencesParams = {
  items: NormalizedAudioSettingsItem[];
  companyId?: number | string | null;
  accountId?: number | string | null;
};

const DEFAULT_VOLUME = 80;

const createDefaultPreference = (toneId: AudioToneId): AudioChannelPreference => ({
  toneId,
  volume: DEFAULT_VOLUME,
});

const readStoredPreference = (key: string, fallback: AudioToneId): AudioChannelPreference => {
  if (typeof window === "undefined") return createDefaultPreference(fallback);

  try {
    const stored = window.localStorage.getItem(key);

    if (!stored) return createDefaultPreference(fallback);

    try {
      const parsed = JSON.parse(stored);

      if (typeof parsed === "object" && parsed !== null) {
        return {
          toneId: typeof parsed.toneId === "string" ? parsed.toneId : fallback,
          volume: typeof parsed.volume === "number" ? parsed.volume : DEFAULT_VOLUME,
        };
      }
    } catch {
      return createDefaultPreference(stored);
    }

    return createDefaultPreference(fallback);
  } catch {
    return createDefaultPreference(fallback);
  }
};

export const useAudioPreferences = ({ items, companyId, accountId }: UseAudioPreferencesParams) => {
  const preferenceKeys = useMemo(
    () =>
      items.reduce<Record<string, string>>((acc, item) => {
        acc[item.channel] = getAudioPreferenceKey({
          channel: item.channel,
          scope: item.scope,
          companyId,
          accountId,
        });

        return acc;
      }, {}),
    [accountId, companyId, items],
  );

  const [preferences, setPreferences] = useState<Record<string, AudioChannelPreference>>(() =>
    items.reduce<Record<string, AudioChannelPreference>>((acc, item) => {
      acc[item.channel] = readStoredPreference(preferenceKeys[item.channel], DEFAULT_AUDIO_TONE_BY_CHANNEL[item.channel]);
      return acc;
    }, {}),
  );

  useEffect(() => {
    setPreferences(
      items.reduce<Record<string, AudioChannelPreference>>((acc, item) => {
        acc[item.channel] = readStoredPreference(preferenceKeys[item.channel], DEFAULT_AUDIO_TONE_BY_CHANNEL[item.channel]);
        return acc;
      }, {}),
    );
  }, [items, preferenceKeys]);

  const updatePreference = useCallback(
    (channel: string, nextPartial: Partial<AudioChannelPreference>) => {
      setPreferences((current) => {
        const currentPreference =
          current[channel] ?? createDefaultPreference(DEFAULT_AUDIO_TONE_BY_CHANNEL[channel as keyof typeof DEFAULT_AUDIO_TONE_BY_CHANNEL]);
        const nextPreference = { ...currentPreference, ...nextPartial };

        if (typeof window !== "undefined") {
          const key = preferenceKeys[channel];
          if (key) {
            window.localStorage.setItem(key, JSON.stringify(nextPreference));
          }
        }

        return { ...current, [channel]: nextPreference };
      });
    },
    [preferenceKeys],
  );

  return {
    preferences,
    updatePreference,
  };
};
